import React from 'react';
import { ActivitySquare, Scale, Heart, Info, MessageSquare, TrendingUp, ArrowRight, AlertTriangle, Ruler } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета индекса талия-бедра
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Получаем цвет категории
  const categoryColor = results.category.color === '#4caf50' ? 'green' : 
                        results.category.color === '#ffa726' ? 'orange' : 'red';

  // Определение текста для интерпретации результатов
  let interpretationText = '';
  
  if (results.category.id.includes('normal')) {
    interpretationText = 'У вас здоровое соотношение талии к бедрам. Продолжайте поддерживать сбалансированное питание и регулярную физическую активность.';
  } else if (results.category.id.includes('low')) {
    interpretationText = 'У вас соотношение талия-бедра ниже нормы. Это может быть связано с особенностями телосложения. Обратите внимание на рекомендации по питанию и физической активности.';
  } else if (results.category.id.includes('high')) {
    interpretationText = 'У вас повышенное соотношение талия-бедра, что может быть связано с избыточным накоплением висцерального жира. Рекомендуется обратить внимание на питание и физическую активность.';
  }

  // Определение текста для рисков
  let riskText = results.category.risk;
  
  return (
    <ToolResult title="Результаты расчета индекса талия-бедра (WHR)">
      {/* Основной показатель WHR */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        <div className={`rounded-lg bg-gradient-to-br from-${categoryColor}-50 to-${categoryColor}-100 dark:from-${categoryColor}-900/30 dark:to-${categoryColor}-800/30 p-5 border border-${categoryColor}-200 dark:border-${categoryColor}-800/50 shadow-sm`}>
          <h3 className={`text-base font-medium text-${categoryColor}-700 dark:text-${categoryColor}-300 mb-1.5 flex items-center gap-1.5`}>
            <ActivitySquare className="w-4 h-4"/>
            Индекс талия-бедра (WHR)
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`text-3xl font-bold text-${categoryColor}-800 dark:text-${categoryColor}-200`} aria-live="polite">
              {formatNumber(results.whr, 2)}
            </span>
            <span className={`text-sm font-medium text-${categoryColor}-600 dark:text-${categoryColor}-400`}>
              {results.category.name}
            </span>
          </div>
          <p className={`text-xs text-${categoryColor}-600 dark:text-${categoryColor}-400 mt-1`}>
            {interpretationText}
          </p>
        </div>

        {/* ИМТ */}
        <div className={`rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 p-5 border border-blue-200 dark:border-blue-800/50 shadow-sm`}>
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Scale className="w-4 h-4"/>
            Индекс массы тела (ИМТ)
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatNumber(results.bmi, 1)}
            </span>
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
              {results.bmiCategory.name}
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            {results.bmiCategory.risk}
          </p>
        </div>
      </div>

      {/* Факторы риска */}
      <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
              Факторы риска для здоровья
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              {riskText}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
              <div className="flex items-start gap-2">
                <Heart className="w-4 h-4 text-red-500 mt-0.5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {results.category.id.includes('high')
                    ? 'Повышенный риск сердечно-сосудистых заболеваний'
                    : 'Низкий риск сердечно-сосудистых заболеваний'}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <MessageSquare className="w-4 h-4 text-indigo-500 mt-0.5" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {results.category.id.includes('high')
                    ? 'Повышенный риск метаболического синдрома'
                    : 'Низкий риск метаболического синдрома'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Соотношение талия-бедра (WHR)" 
          value={formatNumber(results.whr, 2)} 
          color={categoryColor} 
          icon={<ActivitySquare className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Категория WHR" 
          value={results.category.name} 
          color={categoryColor}
        />
        
        <ResultItem 
          label="Обхват талии" 
          value={`${results.measurements.waist} ${results.measurements.measurementSystem === 'metric' ? 'см' : 'дюйм'}`} 
          icon={<Ruler className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Обхват бедер" 
          value={`${results.measurements.hip} ${results.measurements.measurementSystem === 'metric' ? 'см' : 'дюйм'}`} 
          icon={<Ruler className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Индекс массы тела (ИМТ)" 
          value={`${formatNumber(results.bmi, 1)} (${results.bmiCategory.name})`} 
          color="blue"
          icon={<Scale className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Соотношение талии к росту" 
          value={formatNumber(results.measurements.waistToHeightRatio, 2)} 
          color="purple"
        />
      </div>

      {/* Идеальные параметры */}
      <div className="mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-100 dark:border-emerald-800">
        <h4 className="text-sm font-medium text-emerald-700 dark:text-emerald-300 mb-2 flex items-center">
          <TrendingUp className="w-4 h-4 mr-1.5" />
          Рекомендуемые параметры для вашего роста
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 p-3 rounded-md shadow-sm border border-emerald-200 dark:border-emerald-800/70">
            <div className="text-xs text-emerald-600 dark:text-emerald-400 mb-1">Рекомендуемый WHR</div>
            <div className="text-lg font-medium text-gray-900 dark:text-white">{results.idealMeasurements.whr}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 rounded-md shadow-sm border border-emerald-200 dark:border-emerald-800/70">
            <div className="text-xs text-emerald-600 dark:text-emerald-400 mb-1">Рекомендуемая талия</div>
            <div className="text-lg font-medium text-gray-900 dark:text-white">
              {results.idealMeasurements.waist} {results.measurements.measurementSystem === 'metric' ? 'см' : 'дюйм'}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-3 rounded-md shadow-sm border border-emerald-200 dark:border-emerald-800/70">
            <div className="text-xs text-emerald-600 dark:text-emerald-400 mb-1">Рекомендуемые бедра</div>
            <div className="text-lg font-medium text-gray-900 dark:text-white">
              {results.idealMeasurements.hip} {results.measurements.measurementSystem === 'metric' ? 'см' : 'дюйм'}
            </div>
          </div>
        </div>
        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-3">
          Эти значения являются ориентировочными и основаны на общих рекомендациях для вашего роста. 
          Индивидуальные особенности строения тела могут отличаться.
        </p>
      </div>

      {/* Персональные рекомендации */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-100 dark:border-blue-800">
        <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2 flex items-center">
          <Info className="w-4 h-4 mr-1.5" />
          Персональные рекомендации
        </h4>
        
        <div className="space-y-4">
          {/* Рекомендации по питанию */}
          <div className="bg-white dark:bg-gray-800 p-3 rounded-md shadow-sm border border-blue-200 dark:border-blue-800/70">
            <h5 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">Питание</h5>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 list-disc list-inside">
              {results.recommendations.nutrition.map((rec, index) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>
          
          {/* Рекомендации по физической активности */}
          <div className="bg-white dark:bg-gray-800 p-3 rounded-md shadow-sm border border-blue-200 dark:border-blue-800/70">
            <h5 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">Физическая активность</h5>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 list-disc list-inside">
              {results.recommendations.activity.map((rec, index) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>
          
          {/* Рекомендации по образу жизни */}
          <div className="bg-white dark:bg-gray-800 p-3 rounded-md shadow-sm border border-blue-200 dark:border-blue-800/70">
            <h5 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">Образ жизни</h5>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1.5 list-disc list-inside">
              {results.recommendations.lifestyle.map((rec, index) => (
                <li key={index}>{rec}</li>
              ))}
            </ul>
          </div>
        </div>
        
        <p className="text-xs text-blue-600 dark:text-blue-400 mt-3">
          {results.recommendations.note}
        </p>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;